import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent!",
        description: "We'll get back to you as soon as possible.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error sending your message. Please try calling us instead.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-nanny-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nanny-dark mb-6 heading-font">Get In Touch</h1>
            <p className="text-xl text-gray-700">
              Have questions? Want to place an order? We'd love to hear from you!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-nanny-dark mb-8 heading-font">Contact Information</h2>
              
              <div className="space-y-6">
                <Card className="border-2 border-nanny-cream hover:border-nanny-maroon transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-nanny-cream rounded-full p-3">
                        <Phone className="h-6 w-6 text-nanny-maroon" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-nanny-dark mb-1">Phone</h3>
                        <a href="tel:731-267-9884" className="text-gray-700 hover:text-nanny-maroon transition-colors">
                          731-267-9884
                        </a>
                        <p className="text-sm text-gray-500 mt-1">Call or text us anytime</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-nanny-cream hover:border-nanny-maroon transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-nanny-cream rounded-full p-3">
                        <MapPin className="h-6 w-6 text-nanny-maroon" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-nanny-dark mb-1">Location</h3>
                        <p className="text-gray-700">18725 W Main St</p>
                        <p className="text-gray-700">Huntingdon, TN 38344</p>
                        <p className="text-sm text-gray-500 mt-1">Serving Huntingdon and surrounding areas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-nanny-cream hover:border-nanny-maroon transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-nanny-cream rounded-full p-3">
                        <Clock className="h-6 w-6 text-nanny-maroon" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-nanny-dark mb-1">Hours</h3>
                        <div className="space-y-1">
                          <p className="text-gray-700"><span className="font-medium">In-Store:</span> Mon-Thu, 10:30 AM - 5:30 PM</p>
                          <p className="text-gray-700"><span className="font-medium">Catering:</span> 7 Days a Week</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-nanny-cream hover:border-nanny-maroon transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-nanny-cream rounded-full p-3">
                        <Facebook className="h-6 w-6 text-nanny-maroon" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-nanny-dark mb-1">Follow Us</h3>
                        <a 
                          href="https://www.facebook.com/profile.php?id=100068874377725" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-700 hover:text-nanny-maroon transition-colors"
                        >
                          Check Facebook for weekly menu
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-nanny-dark mb-8 heading-font">Send Us a Message</h2>
              <Card className="border-2 border-nanny-cream">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="731-267-9884"
                        className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your inquiry or catering needs..."
                        rows={6}
                        className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-nanny-maroon hover:bg-nanny-dark text-white py-6 text-lg"
                    >
                      {isSubmitting ? (
                        <span>Sending...</span>
                      ) : (
                        <span className="flex items-center justify-center">
                          Send Message
                          <Send className="ml-2 h-5 w-5" />
                        </span>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-20 bg-nanny-maroon text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6 heading-font">Prefer to Call?</h2>
          <p className="text-xl mb-8 text-nanny-cream max-w-2xl mx-auto">
            We're always happy to chat! Give us a call to place an order or discuss your catering needs.
          </p>
          <a href="tel:731-267-9884">
            <Button size="lg" className="bg-white text-nanny-maroon hover:bg-nanny-cream text-xl px-10 py-6">
              <Phone className="mr-2 h-6 w-6" />
              Call 731-267-9884
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
